/**
 * The one delete confirmation (spec 003; spec 002 reuses it for content
 * folders). Never `window.confirm`. Each action runs async; while it runs the
 * buttons are disabled, a thrown error is shown in the modal and keeps it
 * open, success closes it.
 */
import { ReactNode, useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';

export interface ConfirmDeleteAction {
  key: string;
  label: string;
  variant?: 'danger' | 'warning' | 'primary' | 'outline-danger';
  /** The default action gets focus, so Enter confirms it. */
  isDefault?: boolean;
  run: () => Promise<void> | void;
}

export interface ConfirmDeleteModalProps {
  show: boolean;
  title: string;
  children?: ReactNode;
  /** Names of the things that still use what is being deleted ("Used in: …"). */
  usedIn?: string[];
  /** What happens to them, e.g. "Those scenes will show a missing image." */
  usedInWarning?: string;
  /** Shown while the caller is still finding out what uses the item. */
  checking?: boolean;
  actions: ConfirmDeleteAction[];
  onClose: () => void;
}

const ConfirmDeleteModal = ({
  show,
  title,
  children,
  usedIn,
  usedInWarning,
  checking = false,
  actions,
  onClose,
}: ConfirmDeleteModalProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setBusy(false);
      setError(null);
    }
  }, [show]);

  const runAction = async (action: ConfirmDeleteAction) => {
    setBusy(true);
    setError(null);
    try {
      await action.run();
      setBusy(false);
      onClose();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal show={show} onHide={() => !busy && onClose()} centered data-testid="confirm-delete-modal">
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {children}
        {checking && <p className="text-muted small mb-0">Checking where this is used…</p>}
        {!checking && usedIn && usedIn.length > 0 && (
          <Alert variant="warning" className="mt-2 mb-0" data-testid="used-in">
            <strong>Used in:</strong> {usedIn.join(', ')}
            {usedInWarning && <div className="small mt-1">{usedInWarning}</div>}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-2 mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {actions.map((action) => (
          <Button
            key={action.key}
            variant={action.variant ?? 'danger'}
            disabled={busy || checking}
            autoFocus={action.isDefault}
            onClick={() => runAction(action)}
          >
            {action.label}
          </Button>
        ))}
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDeleteModal;

export interface ConfirmFolderDeleteModalProps {
  show: boolean;
  folderName: string;
  /**
   * Everything inside, recursively. `items` counts one kind (media files by
   * default); `kinds` replaces it with several (scenes, stories) for the
   * content organiser (spec 002).
   */
  contents: {
    folders: number;
    items: number;
    itemLabel?: [singular: string, plural: string];
    kinds?: { count: number; one: string; many: string }[];
  };
  /** Still loading the counts: the actions wait. */
  checking?: boolean;
  /** Things outside the folder that "Delete everything" also affects. */
  usedIn?: string[];
  usedInWarning?: string;
  /** "Move contents up and delete" (the default action). */
  onMoveContentsUp: () => Promise<void> | void;
  /** "Delete everything". */
  onDeleteEverything: () => Promise<void> | void;
  onClose: () => void;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "a", "a and b", "a, b and c". */
const joinList = (parts: string[]) =>
  parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/** Folder delete with the two modes; shared by the media library and the content organiser. */
export const ConfirmFolderDeleteModal = ({
  show,
  folderName,
  contents,
  checking = false,
  usedIn,
  usedInWarning,
  onMoveContentsUp,
  onDeleteEverything,
  onClose,
}: ConfirmFolderDeleteModalProps) => {
  const [one, many] = contents.itemLabel ?? ['media file', 'media files'];
  const kinds = contents.kinds ?? [{ count: contents.items, one, many }];
  const parts = [
    ...kinds.filter((k) => k.count > 0).map((k) => plural(k.count, k.one, k.many)),
    contents.folders > 0 ? plural(contents.folders, 'subfolder', 'subfolders') : null,
  ].filter((p): p is string => Boolean(p));
  const empty = parts.length === 0;

  return (
    <ConfirmDeleteModal
      show={show}
      title={`Delete folder "${folderName}"?`}
      onClose={onClose}
      checking={checking}
      usedIn={usedIn}
      usedInWarning={usedInWarning}
      actions={[
        { key: 'move-up', label: 'Move contents up and delete', variant: 'warning', isDefault: true, run: onMoveContentsUp },
        { key: 'delete-all', label: 'Delete everything', variant: 'danger', run: onDeleteEverything },
      ]}
    >
      {checking ? null : empty ? (
        <p className="mb-0">The folder is empty.</p>
      ) : (
        <p className="mb-0" data-testid="folder-contents">
          It contains {joinList(parts)}. "Move contents up" keeps them in the parent folder; "Delete everything"
          removes them for good.
        </p>
      )}
    </ConfirmDeleteModal>
  );
};
